// 10 soru tipi için tipe-özel options editörleri — plan §5.4 + Ek A + M15 composite.
// Her editör `options`'ı okur ve `onChange(newOptions)` callback'i çağırır.

import { useRef, useState } from "react";
import type { CompositePart, CompositeQuestionOptions, QuestionOptions } from "@mahir/shared";
import { defaultOptionsForType, NUMERIC_UNITS } from "@mahir/shared";

// M15 E2 — seçenek ID otomatik slug (§4.6). TR harita + küçült + alfanümerik dışı → "-".
const TR_SLUG_MAP: Record<string, string> = {
  ç: "c",
  ğ: "g",
  ı: "i",
  ö: "o",
  ş: "s",
  ü: "u",
  â: "a",
  î: "i",
  û: "u",
};

export function slugifyOptionLabel(label: string): string {
  const lowered = label.toLocaleLowerCase("tr-TR");
  let mapped = "";
  for (const ch of lowered) mapped += TR_SLUG_MAP[ch] ?? ch;
  return mapped.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function uniqueSlug(base: string, taken: ReadonlySet<string>): string {
  const root = base.length > 0 ? base : "secenek";
  if (!taken.has(root)) return root;
  for (let n = 2; ; n++) {
    const candidate = `${root}-${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

interface Props {
  options: QuestionOptions;
  onChange: (next: QuestionOptions) => void;
  disabled?: boolean | undefined;
}

const inputStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid var(--color-border)",
  fontSize: "0.85rem",
  fontFamily: "var(--font-sans)",
  background: "var(--color-bg)",
};
const labelStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--color-text-3)",
  display: "block",
  marginBottom: 4,
};
const removeBtnStyle: React.CSSProperties = {
  padding: "4px 8px",
  fontSize: "0.7rem",
  border: "1px solid var(--color-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--color-error)",
  cursor: "pointer",
};
const addBtnStyle: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "0.8rem",
  border: "1px dashed var(--color-border)",
  borderRadius: 6,
  background: "transparent",
  color: "var(--color-primary)",
  cursor: "pointer",
};

export function QuestionTypeEditor({ options, onChange, disabled }: Props): JSX.Element {
  switch (options.type) {
    case "text":
      return (
        <div>
          <label style={labelStyle}>Maksimum karakter sayısı</label>
          <input
            type="number"
            value={options.maxLength}
            min={1}
            max={50000}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...options, maxLength: Math.max(1, Number(e.target.value)) })
            }
            style={{ ...inputStyle, width: 140 }}
          />
        </div>
      );

    case "numeric": {
      // unit tri-state: undefined = "yok", "" = "özel seçildi henüz yazılmadı",
      // preset = listede, diğer string = özel değer. Bu ayrım sayesinde "Özel…"
      // seçimi select'te sabit kalır (yoksa "— yok —"a düşer).
      const unit = options.unit;
      const isNone = unit === undefined;
      const isPreset =
        typeof unit === "string" && unit !== "" && NUMERIC_UNITS.includes(unit as never);
      const selectValue = isNone ? "" : isPreset ? unit : "__custom__";
      const showCustomInput = !isNone && !isPreset;
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Birim</label>
            <select
              value={selectValue}
              disabled={disabled}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") {
                  onChange({ ...options, unit: undefined });
                } else if (v === "__custom__") {
                  // "Özel" seçildi — boş string sentinel'i koyulur, kullanıcı
                  // custom input'a yazdıkça üstüne yazılır; select "Özel…" kalır.
                  onChange({ ...options, unit: "" });
                } else {
                  onChange({ ...options, unit: v });
                }
              }}
              style={{ ...inputStyle, width: "100%" }}
            >
              <option value="">— yok —</option>
              {NUMERIC_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
              <option value="__custom__">Özel…</option>
            </select>
            {showCustomInput ? (
              <input
                type="text"
                placeholder="Özel birim (ör. ppm, tCO₂e)"
                value={unit ?? ""}
                disabled={disabled}
                onChange={(e) => onChange({ ...options, unit: e.target.value })}
                style={{ ...inputStyle, width: "100%", marginTop: 6 }}
              />
            ) : null}
          </div>
          <div>
            <label style={labelStyle}>Min</label>
            <input
              type="number"
              value={options.min ?? ""}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  ...options,
                  min: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              style={{ ...inputStyle, width: "100%" }}
            />
          </div>
          <div>
            <label style={labelStyle}>Max</label>
            <input
              type="number"
              value={options.max ?? ""}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  ...options,
                  max: e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              style={{ ...inputStyle, width: "100%" }}
            />
          </div>
        </div>
      );
    }

    case "single":
    case "multi":
      return (
        <ChoiceListEditor
          label="Seçenekler"
          items={options.choices}
          onChange={(next) => onChange({ ...options, choices: next })}
          disabled={disabled}
          allowCustomInputToggle
        />
      );

    case "percent":
      return (
        <ChoiceListEditor
          label="Kategoriler (toplam %100 olmalı)"
          items={options.categories}
          onChange={(next) => onChange({ ...options, categories: next })}
          disabled={disabled}
        />
      );

    case "date":
      return (
        <div>
          <label style={labelStyle}>Tarih formatı</label>
          <select
            value={options.format}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...options, format: e.target.value as "YYYY-MM-DD" | "MM-YYYY" })
            }
            style={{ ...inputStyle, width: 200 }}
          >
            <option value="YYYY-MM-DD">YYYY-MM-DD (gün/ay/yıl)</option>
            <option value="MM-YYYY">MM-YYYY (ay/yıl)</option>
          </select>
        </div>
      );

    case "file":
      return (
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={labelStyle}>Kabul edilen MIME tipleri (virgülle)</label>
            <input
              type="text"
              value={options.acceptedMimeTypes.join(", ")}
              disabled={disabled}
              placeholder="application/pdf, image/png"
              onChange={(e) =>
                onChange({
                  ...options,
                  acceptedMimeTypes: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              style={{ ...inputStyle, width: "100%" }}
            />
          </div>
          <div>
            <label style={labelStyle}>Maksimum dosya boyutu (MB)</label>
            <input
              type="number"
              value={options.maxSizeMb}
              min={1}
              max={1024}
              disabled={disabled}
              onChange={(e) =>
                onChange({ ...options, maxSizeMb: Math.max(1, Number(e.target.value)) })
              }
              style={{ ...inputStyle, width: 140 }}
            />
          </div>
        </div>
      );

    case "table":
      return (
        <div>
          <label style={labelStyle}>Sütunlar</label>
          <div style={{ display: "grid", gap: 8 }}>
            {options.columns.map((c, i) => (
              // NOT: key={i} — kullanıcı ID input'u yazarken key değişmesin diye
              // (aksi halde React input'u unmount/remount eder → focus kaybı).
              // #006: numeric için "Birim" sütunu eklendi. Diğer tiplerde input
              // disabled (görünüm tutarlı kalsın).
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr auto",
                  gap: 8,
                }}
              >
                <input
                  type="text"
                  placeholder="Sütun kodu"
                  value={c.id}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...options.columns];
                    next[i] = { ...c, id: e.target.value };
                    onChange({ ...options, columns: next });
                  }}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Sütun adı"
                  value={c.label}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...options.columns];
                    next[i] = { ...c, label: e.target.value };
                    onChange({ ...options, columns: next });
                  }}
                  style={inputStyle}
                />
                <select
                  value={c.type}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...options.columns];
                    const newType = e.target.value as "text" | "numeric" | "date";
                    // Tip numeric'ten farklı bir şeye değişirse unit temizle
                    next[i] = {
                      ...c,
                      type: newType,
                      ...(newType === "numeric" ? {} : { unit: undefined }),
                    };
                    onChange({ ...options, columns: next });
                  }}
                  style={inputStyle}
                >
                  <option value="text">Metin</option>
                  <option value="numeric">Sayı</option>
                  <option value="date">Tarih</option>
                </select>
                <input
                  type="text"
                  placeholder={c.type === "numeric" ? "Birim (ör. ton)" : "—"}
                  value={c.unit ?? ""}
                  disabled={Boolean(disabled) || c.type !== "numeric"}
                  onChange={(e) => {
                    const next = [...options.columns];
                    next[i] = { ...c, unit: e.target.value || undefined };
                    onChange({ ...options, columns: next });
                  }}
                  title={c.type === "numeric" ? "Birim (opsiyonel)" : "Sadece sayı tipli sütunda"}
                  style={inputStyle}
                />
                <button
                  disabled={Boolean(disabled) || options.columns.length <= 1}
                  onClick={() => {
                    const next = options.columns.filter((_, ix) => ix !== i);
                    onChange({ ...options, columns: next });
                  }}
                  style={removeBtnStyle}
                >
                  Sil
                </button>
              </div>
            ))}
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...options,
                  columns: [
                    ...options.columns,
                    {
                      id: `c${options.columns.length + 1}`,
                      label: `Sütun ${options.columns.length + 1}`,
                      type: "text",
                    },
                  ],
                })
              }
              style={addBtnStyle}
            >
              + Sütun Ekle
            </button>
          </div>
        </div>
      );

    case "repeater":
      return (
        <div>
          <label style={labelStyle}>Tekrarlanan alt-alanlar</label>
          <div style={{ display: "grid", gap: 8 }}>
            {options.subFields.map((f, i) => (
              // NOT: key={i} — ID input fokus kaybı fix'i (bkz table branch).
              <div
                key={i}
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8 }}
              >
                <input
                  type="text"
                  placeholder="Alan kodu"
                  value={f.id}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...options.subFields];
                    next[i] = { ...f, id: e.target.value };
                    onChange({ ...options, subFields: next });
                  }}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Alan adı"
                  value={f.label}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...options.subFields];
                    next[i] = { ...f, label: e.target.value };
                    onChange({ ...options, subFields: next });
                  }}
                  style={inputStyle}
                />
                <select
                  value={f.type}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...options.subFields];
                    next[i] = { ...f, type: e.target.value as "text" | "numeric" | "date" };
                    onChange({ ...options, subFields: next });
                  }}
                  style={inputStyle}
                >
                  <option value="text">Metin</option>
                  <option value="numeric">Sayı</option>
                  <option value="date">Tarih</option>
                </select>
                <button
                  disabled={Boolean(disabled) || options.subFields.length <= 1}
                  onClick={() => {
                    const next = options.subFields.filter((_, ix) => ix !== i);
                    onChange({ ...options, subFields: next });
                  }}
                  style={removeBtnStyle}
                >
                  Sil
                </button>
              </div>
            ))}
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...options,
                  subFields: [
                    ...options.subFields,
                    {
                      id: `f${options.subFields.length + 1}`,
                      label: `Alan ${options.subFields.length + 1}`,
                      type: "text",
                    },
                  ],
                })
              }
              style={addBtnStyle}
            >
              + Alan Ekle
            </button>
          </div>
        </div>
      );

    case "composite":
      return <CompositeEditor options={options} onChange={onChange} disabled={disabled} />;
  }
}

// ---------------------------------------------------------------------------
// M15 — CompositeEditor (§4.2): açılır parça listesi, aynı anda tek parça açık.
// Her parça kendi tam QuestionTypeEditor'ünü taşır (derinlik 1 — parça options'ı
// composite olamaz, şemadan garanti).
// ---------------------------------------------------------------------------

const PART_TYPE_LABELS: Record<CompositePart["options"]["type"], string> = {
  text: "Metin",
  numeric: "Sayısal",
  single: "Tek Seçim",
  multi: "Çoklu Seçim",
  date: "Tarih",
  file: "Dosya",
  table: "Tablo",
  percent: "Yüzde",
  repeater: "Tekrarlanabilir",
};
const PART_TYPES = Object.keys(PART_TYPE_LABELS) as CompositePart["options"]["type"][];

interface CompositeEditorProps {
  options: CompositeQuestionOptions;
  onChange: (next: QuestionOptions) => void;
  disabled: boolean | undefined;
}

function CompositeEditor({ options, onChange, disabled }: CompositeEditorProps): JSX.Element {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const parts = options.parts;
  const hasFilePart = parts.some((p) => p.options.type === "file");

  const updatePart = (i: number, patch: Partial<CompositePart>): void => {
    const next = [...parts];
    next[i] = { ...next[i]!, ...patch };
    onChange({ ...options, parts: next });
  };

  const movePart = (i: number, dir: -1 | 1): void => {
    const j = i + dir;
    if (j < 0 || j >= parts.length) return;
    const next = [...parts];
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
    onChange({ ...options, parts: next });
    if (openIdx === i) setOpenIdx(j);
    else if (openIdx === j) setOpenIdx(i);
  };

  const removePart = (i: number): void => {
    if (parts.length <= 1) return;
    onChange({ ...options, parts: parts.filter((_, ix) => ix !== i) });
    if (openIdx === i) setOpenIdx(null);
    else if (openIdx !== null && openIdx > i) setOpenIdx(openIdx - 1);
  };

  const addPart = (): void => {
    if (parts.length >= 10) return;
    const taken = new Set(parts.map((p) => p.key));
    let n = parts.length + 1;
    while (taken.has(`alan${n}`)) n += 1;
    const next: CompositePart = {
      key: `alan${n}`,
      label: `Alan ${n}`,
      required: false,
      options: { type: "text", maxLength: 5000 },
    };
    onChange({ ...options, parts: [...parts, next] });
    setOpenIdx(parts.length);
  };

  return (
    <div>
      <div
        style={{
          display: "grid",
          gap: 0,
          border: "1px solid var(--color-border)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {parts.map((part, i) => {
          const open = openIdx === i;
          const isDupKey = parts.some((p, ix) => ix !== i && p.key === part.key);
          const panelId = `composite-part-panel-${i}`;
          return (
            // key={i} — parça kodu yazılırken input unmount olmasın (focus kaybı fix'i,
            // table/repeater editörlerindeki bilinen tuzakla aynı kural)
            <div key={i} style={i > 0 ? { borderTop: "1px solid var(--color-border)" } : undefined}>
              <div style={{ display: "flex", alignItems: "stretch" }}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => setOpenIdx(open ? null : i)}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    minHeight: 44,
                    background: open ? "var(--color-bg)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "0.85rem",
                    color: "var(--color-text)",
                  }}
                >
                  <span aria-hidden style={{ fontSize: "0.7rem", color: "var(--color-text-2)" }}>
                    {open ? "▾" : "▸"}
                  </span>
                  <span style={{ fontSize: "0.78rem", color: "var(--color-text-2)" }}>
                    {part.key}
                  </span>
                  <span style={{ fontWeight: 600 }}>{part.label}</span>
                  <span
                    style={{
                      fontSize: "0.72rem",
                      color: "var(--color-text-2)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 4,
                      padding: "1px 6px",
                    }}
                  >
                    {PART_TYPE_LABELS[part.options.type]}
                  </span>
                  {part.required && (
                    <span
                      title="Zorunlu parça"
                      style={{ color: "var(--color-error)", fontWeight: 700 }}
                    >
                      ✱
                    </span>
                  )}
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 4, paddingRight: 8 }}>
                  <button
                    type="button"
                    aria-label="Parçayı yukarı taşı"
                    disabled={Boolean(disabled) || i === 0}
                    onClick={() => movePart(i, -1)}
                    style={{ ...removeBtnStyle, color: "var(--color-text-2)" }}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    aria-label="Parçayı aşağı taşı"
                    disabled={Boolean(disabled) || i === parts.length - 1}
                    onClick={() => movePart(i, 1)}
                    style={{ ...removeBtnStyle, color: "var(--color-text-2)" }}
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(disabled) || parts.length <= 1}
                    title={parts.length <= 1 ? "En az bir parça kalmalı" : "Parçayı sil"}
                    onClick={() => removePart(i)}
                    style={removeBtnStyle}
                  >
                    Sil
                  </button>
                </div>
              </div>
              {open && (
                <div
                  id={panelId}
                  style={{
                    padding: "12px 12px 16px 34px",
                    background: "var(--color-bg)",
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={labelStyle}>Parça kodu</label>
                      <input
                        type="text"
                        value={part.key}
                        disabled={disabled}
                        onChange={(e) =>
                          updatePart(i, {
                            key: e.target.value
                              .toLocaleLowerCase("tr-TR")
                              .replace(/[^a-z0-9_]/g, ""),
                          })
                        }
                        style={{ ...inputStyle, width: "100%" }}
                      />
                      {isDupKey && (
                        <p
                          style={{ color: "var(--color-error)", fontSize: "0.72rem", marginTop: 4 }}
                        >
                          Bu parça kodu başka bir parçada kullanılıyor
                        </p>
                      )}
                    </div>
                    <div>
                      <label style={labelStyle}>Parça adı</label>
                      <input
                        type="text"
                        value={part.label}
                        disabled={disabled}
                        onChange={(e) => updatePart(i, { label: e.target.value })}
                        style={{ ...inputStyle, width: "100%" }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Yardımcı not</label>
                    <input
                      type="text"
                      value={part.helperText ?? ""}
                      disabled={disabled}
                      placeholder="Parça altında gösterilecek yardımcı metin"
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.length === 0) {
                          const { helperText: _drop, ...rest } = part;
                          const next = [...parts];
                          next[i] = rest;
                          onChange({ ...options, parts: next });
                        } else {
                          updatePart(i, { helperText: v });
                        }
                      }}
                      style={{ ...inputStyle, width: "100%" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <div>
                      <label style={labelStyle}>Tip</label>
                      <select
                        value={part.options.type}
                        disabled={disabled}
                        onChange={(e) => {
                          const t = e.target.value as CompositePart["options"]["type"];
                          if (t === part.options.type) return;
                          // Tip değişimi options'ı sıfırlar, confirm sormaz (§4.2)
                          updatePart(i, {
                            options: defaultOptionsForType(t) as CompositePart["options"],
                          });
                        }}
                        style={{ ...inputStyle, width: 200 }}
                      >
                        {PART_TYPES.map((t) => {
                          const fileBlocked =
                            t === "file" && hasFilePart && part.options.type !== "file";
                          return (
                            <option
                              key={t}
                              value={t}
                              disabled={fileBlocked}
                              title={
                                fileBlocked
                                  ? "Bileşik soruda en fazla bir dosya parçası olabilir"
                                  : undefined
                              }
                            >
                              {PART_TYPE_LABELS[t]}
                              {fileBlocked ? " (kullanımda)" : ""}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.82rem",
                        color: "var(--color-text)",
                        marginTop: 16,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={part.required}
                        disabled={disabled}
                        onChange={(e) => updatePart(i, { required: e.target.checked })}
                      />
                      Zorunlu
                    </label>
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--color-text-2)",
                        borderTop: "1px dashed var(--color-border)",
                        paddingTop: 10,
                        marginBottom: 8,
                      }}
                    >
                      {PART_TYPE_LABELS[part.options.type]} — Ayarlar
                    </div>
                    {/* key={part.key} — parça sıralaması değişince iç editör (ve
                        ChoiceListEditor autoLinked ref'i) parçayla birlikte taşınsın */}
                    <QuestionTypeEditor
                      key={part.key}
                      options={part.options}
                      onChange={(o) => updatePart(i, { options: o as CompositePart["options"] })}
                      disabled={disabled}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
        <button
          type="button"
          disabled={Boolean(disabled) || parts.length >= 10}
          title={parts.length >= 10 ? "En fazla 10 parça eklenebilir" : undefined}
          onClick={addPart}
          style={addBtnStyle}
        >
          + Parça Ekle
        </button>
        <span style={{ fontSize: "0.72rem", color: "var(--color-text-2)" }}>
          ✱ = zorunlu parça · en fazla 10 parça, 1 dosya parçası
        </span>
      </div>
    </div>
  );
}

interface ChoiceListItem {
  id: string;
  label: string;
  allowCustomInput: boolean;
}
interface ChoiceListProps {
  label: string;
  items: ChoiceListItem[];
  onChange: (next: ChoiceListItem[]) => void;
  disabled: boolean | undefined;
  /** M5.5 — sadece single/multi'de allowCustomInput checkbox göster (percent'te gizle) */
  allowCustomInputToggle?: boolean;
}

function ChoiceListEditor({
  label,
  items,
  onChange,
  disabled,
  allowCustomInputToggle,
}: ChoiceListProps): JSX.Element {
  // M15 E2 — otomatik slug bağı: yalnızca BU oturumda eklenen ve ID'sine elle
  // dokunulmamış seçeneklerde etiket → ID türetilir. Mevcut kayıtların ID'leri
  // (opt1 vb.) sessizce yeniden yazılMAZ; ilk manuel ID düzenlemesi bağı kalıcı koparır.
  const autoLinked = useRef<Set<number>>(new Set());

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: "grid", gap: 8 }}>
        {items.map((it, i) => (
          // NOT: key={i} — ID input fokus kaybı fix'i (bkz editor table branch).
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: allowCustomInputToggle
                ? "140px 1fr auto auto"
                : "160px 1fr auto",
              gap: 8,
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="ID"
              value={it.id}
              disabled={disabled}
              onChange={(e) => {
                autoLinked.current.delete(i); // elle düzenleme — bağ kalıcı kopar
                const next = [...items];
                next[i] = { ...it, id: e.target.value };
                onChange(next);
              }}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Etiket"
              value={it.label}
              disabled={disabled}
              onChange={(e) => {
                const next = [...items];
                const newLabel = e.target.value;
                if (autoLinked.current.has(i)) {
                  const taken = new Set(items.filter((_, ix) => ix !== i).map((x) => x.id));
                  next[i] = {
                    ...it,
                    label: newLabel,
                    id: uniqueSlug(slugifyOptionLabel(newLabel), taken),
                  };
                } else {
                  next[i] = { ...it, label: newLabel };
                }
                onChange(next);
              }}
              style={inputStyle}
            />
            {allowCustomInputToggle && (
              <label
                title="Bu seçenek işaretlenince kullanıcıdan açık metin (örn 'Diğer, açıklayınız') ister"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: "0.78rem",
                  color: "var(--color-text-2)",
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="checkbox"
                  checked={it.allowCustomInput}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...items];
                    next[i] = { ...it, allowCustomInput: e.target.checked };
                    onChange(next);
                  }}
                />
                açık metin
              </label>
            )}
            <button
              disabled={Boolean(disabled) || items.length <= 1}
              onClick={() => {
                // Auto-link index'leri silinen satıra göre kaydır
                const shifted = new Set<number>();
                for (const idx of autoLinked.current) {
                  if (idx === i) continue;
                  shifted.add(idx > i ? idx - 1 : idx);
                }
                autoLinked.current = shifted;
                onChange(items.filter((_, ix) => ix !== i));
              }}
              style={removeBtnStyle}
            >
              Sil
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            // M15 E2 — yeni seçenek ID'si etiketten slug (opt1 üretimi bitti);
            // etiket yazıldıkça ID canlı türemeye devam eder (autoLinked).
            const newLabel = `Seçenek ${items.length + 1}`;
            const taken = new Set(items.map((x) => x.id));
            autoLinked.current.add(items.length);
            onChange([
              ...items,
              {
                id: uniqueSlug(slugifyOptionLabel(newLabel), taken),
                label: newLabel,
                allowCustomInput: false,
              },
            ]);
          }}
          style={addBtnStyle}
        >
          + Ekle
        </button>
      </div>
    </div>
  );
}
